import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState, AppDispatch } from "../redux/store";
import { createUser } from "../redux/slices/authSlice";
import {
  TextField,
  Button,
  Typography,
  Box,
  IconButton,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  ListItemText,
  SelectChangeEvent,
  InputAdornment,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import Toast from "../components/Toast";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { getUserRoleNames } from "../redux/slices/userRoleSlice";
import useDetectSession from "../hooks/useDetectSession.hook";
import { inputStyle, pageHeading, primaryBtnStyle } from "../styles/styling";
import AppContainer from "../components/AppContainer";
import { passReqColor } from "../components/ResetPasswordModal";

const CreateUserPage: React.FC = () => {
  const [fullName, setFullName] = useState<string>("");
  const [email, setEmail] = useState<string>("");
  const [emailError, setEmailError] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [role, setRole] = useState<string[]>([]);
  const [toastOpen, setToastOpen] = useState<boolean>(false);
  const [toastMessage, setToastMessage] = useState<string>("");
  const [toastSeverity, setToastSeverity] = useState<"success" | "error">(
    "success"
  );
  const [roleList, setRoleList] = useState<string[]>([]);

  const [isAtLeast8Char, setIsAtleast8Char] = useState<boolean>(false);
  const [hasNum, setHasNum] = useState<boolean>(false);
  const [hasSpecialChar, setHasSpecialChar] = useState<boolean>(false);
  const [hasUpperCase, setHasUpperCase] = useState<boolean>(false);
  const [hasLowerCase, setHasLowerCase] = useState<boolean>(false);

  const passDisabled =
    !isAtLeast8Char ||
    !hasLowerCase ||
    !hasNum ||
    !hasSpecialChar ||
    !hasUpperCase;

  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { status, error } = useSelector((state: RootState) => state.auth);

  const currentUser = useSelector((state: RootState) => state.auth.currentUser);

  useDetectSession();

  const handleToggle = () => {
    setShowPassword((prev) => !prev);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (
      !/^(?!\.)(?!.*\.\.)[a-zA-Z0-9._%+-]+(?<!\.)@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(
        email
      )
    ) {
      setEmailError("Failed to create user: Invalid Email Format.");
      return;
    }

    const userData = {
      fullName,
      email,
      password,
      isAdmin: true, // Determine isAdmin from role
      role,
      createdBy: currentUser?.email,
      active: true,
    };

    try {
      await dispatch(createUser({ data: userData }))
        .unwrap()
        .then(async () => {
          setToastMessage("User created successfully");
          setToastSeverity("success");
          setToastOpen(true);
          await new Promise<void>((resolve) =>
            setTimeout(() => resolve(), 1000)
          );
          navigate("/dashboard");
        });
    } catch (error) {
      setToastMessage("Failed to create user");
      setToastSeverity("error");
      setToastOpen(true);
      await new Promise<void>((resolve) => setTimeout(() => resolve(), 1000));
    }
  };

  const handleToastClose = () => {
    setToastOpen(false);
  };

  const handleBackClick = () => {
    navigate(-1);
  };

  useEffect(() => {
    const fetchRoles = async () => {
      const list = await dispatch(getUserRoleNames()).unwrap();

      if (list) {
        setRoleList(list);
      }
    };

    fetchRoles();
  }, [dispatch]);

  useEffect(() => {
    if (password.trim()) {
      setIsAtleast8Char(password.trim().length >= 8);
      setHasNum(/[0-9]/g.test(password));
      setHasSpecialChar(/[!-/:-@[-`{-~]/.test(password));
      setHasUpperCase(/[A-Z]/.test(password));
      setHasLowerCase(/[a-z]/.test(password));
    }
  }, [password]);

  const onChangeRole = (event: SelectChangeEvent<string[]>) => {
    const value = event.target.value as string[];
    setRole(value);
  };

  return (
    <AppContainer>
      <Box display="flex" alignItems="center" marginBottom={2}>
        <IconButton onClick={handleBackClick}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h4" sx={pageHeading}>
          Create New User
        </Typography>
      </Box>
      <form onSubmit={handleSubmit}>
        <TextField
          label="Full Name"
          value={fullName}
          onChange={(e) => setFullName(e.target.value)}
          fullWidth
          required
          margin="normal"
          sx={inputStyle()}
        />
        <TextField
          label="Email"
          type="email"
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            setEmailError("");
          }}
          fullWidth
          required
          margin="normal"
          sx={inputStyle()}
        />
        <TextField
          label="Password"
          type={showPassword ? "text" : "password"}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          fullWidth
          required
          margin="normal"
          sx={inputStyle()}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={handleToggle} edge="end">
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        <Typography
          color={passReqColor(password.trim().length === 0, isAtLeast8Char)}
        >
          • Must have at least 8 characters*
        </Typography>
        <Typography
          color={passReqColor(password.trim().length === 0, hasUpperCase)}
        >
          • Must have at least 1 uppercase letter*
        </Typography>
        <Typography
          color={passReqColor(password.trim().length === 0, hasLowerCase)}
        >
          • Must have at least 1 lowercase letter*
        </Typography>
        <Typography color={passReqColor(password.trim().length === 0, hasNum)}>
          • Must have at least 1 number*
        </Typography>
        <Typography
          color={passReqColor(password.trim().length === 0, hasSpecialChar)}
        >
          • Must have at least 1 special character*
        </Typography>
        <FormControl fullWidth margin="normal">
          <InputLabel>Role</InputLabel>
          <Select
            value={role}
            onChange={onChangeRole}
            label="Role"
            renderValue={(selected) =>
              Array.isArray(selected) ? selected.join(", ") : selected
            }
          >
            {roleList.map((item) => (
              <MenuItem key={item} value={item}>
                <ListItemText primary={item} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Box sx={{ mt: 2 }}>
          <Button
            type="submit"
            variant="contained"
            fullWidth
            disabled={
              status === "loading" ||
              passDisabled ||
              !fullName.trim() ||
              role.length === 0 ||
              !email.trim()
            }
            sx={primaryBtnStyle}
          >
            {status === "loading" ? "Creating..." : "Create User"}
          </Button>
        </Box>
        {(error || emailError) && (
          <Typography color="error" variant="body2" sx={{ mt: 2 }}>
            {error ?? emailError}
          </Typography>
        )}
      </form>

      <Toast
        open={toastOpen}
        message={toastMessage}
        severity={toastSeverity}
        onClose={handleToastClose}
      />
    </AppContainer>
  );
};

export default CreateUserPage;
